import { map, startCase, isEmpty } from 'lodash';
import {
  CellClassParams,
  ICellRendererParams,
  ValueGetterParams,
} from 'ag-grid-community';

import { rx } from '@app/shared/rx';
import {
  STAND_WORKSHEET_ORDER_TYPE_OPTIONS_FOR_FILTER,
  STAND_WORKSHEET_ORDER_TYPE_OPTIONS_FOR_RENDER,
  STAND_WORKSHEET_STATUS_OPTIONS,
  STAND_WORKSHEET_STATUS_VALUE,
} from '@app/shared/types/enums/stand-worksheet-status.enum';
import {
  FieldList,
  IFormGroupRules,
  IFormRules,
} from '@components/form/services';
import {
  WIDGET_COMPONENT,
  IBaseFormService,
  ENTITY_TYPES,
  TOOLBAR_BUTTON_POSITION,
  DROPDOWN_MENU_POSITION,
  BUTTON_FLEX_ORDER,
  ISite,
  IEvent,
  XUX_GRID_ACTIONS,
  CELL_FILTER,
  ROW_SELECTOR_ID,
  USER_CONFIG_MODULE_NAME,
} from '@shared/types';
import {
  SUGGESTED_ROW_BUFFER,
  TIME_FORMATS,
  WIDGET_FAMILY,
} from '@config/global-constants';
import {
  COMMON_ERROR_MESSAGE_CLASS,
  GENERAL_ERROR_MESSAGE_CLASS,
} from '@app/shared/constants/common-warn-grid-setting';
import {
  getInformationTabledColumn,
  getStandSheetPinnedColumn,
  getStandSheetUnpinnedColumn,
} from '@shared/services/stand-worksheet/stand-worksheet-columns-definition';
import { getFormFieldValue } from '@app/components/form/services/schema-form/schema-form-functions';
import { ACTIONS_PANEL_BUTTON } from '@app/text-content/button-templates';
import { I18n } from '@app/i18n/strings-keys';

export const createFormRules = (
  formService: IBaseFormService<ENTITY_TYPES.STAND_WORKSHEET>,
): IFormRules => {
  const standSheetPinnedColumn = getStandSheetPinnedColumn(formService);
  const standSheetUnpinnedColumn = getStandSheetUnpinnedColumn(formService);
  const informationTabledColumn = getInformationTabledColumn(formService);

  const sections: FieldList = {
    general_section: {
      widget: WIDGET_COMPONENT.panel_group,
      title: I18n.Common.General,
    },
    sales_orders_list_section: {
      widget: WIDGET_COMPONENT.panel_group,
      title: '',
    },
    stand_sheet_list_section: {
      widget: WIDGET_COMPONENT.panel_group,
      title: '',
    },
  };

  const panelGroups: IFormGroupRules[] = [
    {
      name: I18n.Common.General,
      fields: [
        'general_section',
        'sales_orders_list_section',
        'stand_sheet_list_section',
      ],
    },
    {
      id: 'general_section',
      fields: [
        'virtual.has_pending_transfer_message',
        'entity_id',
        'event_id',
        'virtual.event_date',
        'site_id',
        'status',
        'store_number',
        'miscellaneous',
        'cash_count',
      ],
    },
    {
      id: 'sales_orders_list_section',
      fields: ['virtual.sales_orders_list'],
    },
    {
      id: 'stand_sheet_list_section',
      fields: [
        'virtual.information_list',
        'virtual.write_error_data_message',
        'items',
        'virtual.changed_items',
      ],
    },
  ];

  const entryFormColumns = [
    ...standSheetPinnedColumn,
    ...standSheetUnpinnedColumn,
  ];

  const multiSelectable = formService.haveTransferAddViewPermission;

  if (multiSelectable) {
    entryFormColumns.unshift({
      path: ROW_SELECTOR_ID,
      pinned: 'left',
      lockPinned: true,
      lockPosition: true,
    });
  }

  const entryFormActionsButtonConfig = {
    ...ACTIONS_PANEL_BUTTON,
    flexOrder: BUTTON_FLEX_ORDER.LEFTMOST,
    hidden: (selectedItems): boolean => {
      return isEmpty(selectedItems);
    },
    disableWithGrid: false,
    childItems: [
      {
        value: 'transfer_in',
        class: 'xux-action-button btn full-width',
        caption: I18n.Forms.StandWorksheetForm.TransferIn,
        disableWithGrid: false,
        click: formService.getCreateTransferClickHandler({
          isTransferIn: true,
        }),
      },
      {
        value: 'transfer_out',
        class: 'xux-action-button btn full-width',
        caption: I18n.Forms.StandWorksheetForm.TransferOut,
        disableWithGrid: false,
        click: formService.getCreateTransferClickHandler({
          isTransferIn: false,
        }),
      },
    ],
  };

  return {
    defaultGroup: false,
    virtual: {
      type: 'object',
      properties: {
        sales_orders_list: {
          type: 'array',
        },
        information_list: {
          type: 'array',
        },
        event_date: {
          type: 'string',
        },
        /**
         * for print pdf event refer
         */
        event: {
          type: 'object',
        },
        chargeable: {
          type: 'boolean',
        },
        summary_view: {
          type: 'boolean',
        },
        changed_items: {
          type: 'object',
        },
      },
    },
    fields: {
      ...sections,
      entity_id: {
        show: false,
      },
      event_id: {
        title: I18n.EntityNames.Event,
        widget: WIDGET_COMPONENT.select,
        disabled: true,
        required: true,
        class: 'custom-width',
        selectOptions: formService.eventList$.pipe(
          rx.map((items: IEvent[]) =>
            map(items, (item) => ({ text: item.name, value: item.id })),
          ),
        ),
      },
      'virtual.event_date': {
        title: I18n.Forms.StandWorksheetForm.EventDateTime,
        widget: WIDGET_COMPONENT.select,
        disabled: true,
        selectOptions: formService.eventDate$,
        class: 'custom-width',
      },
      site_id: {
        title: I18n.EntityNames.Site,
        widget: WIDGET_COMPONENT.select,
        selectOptions: formService.siteList$.pipe(
          rx.map((items: ISite[]) =>
            map(items, (item) => ({ text: item.name, value: item.id })),
          ),
        ),
        disabled: true,
        class: 'custom-width',
      },
      status: {
        title: I18n.Common.Status,
        widget: WIDGET_COMPONENT.select,
        optionText: STAND_WORKSHEET_STATUS_OPTIONS,
        defaultValue: STAND_WORKSHEET_STATUS_VALUE.Open,
        disabled: true,
        required: false,
        class: 'custom-width',
      },
      'virtual.sales_orders_list': {
        widget: WIDGET_COMPONENT.entity_form_list,
        title: I18n.Forms.StandWorksheetForm.SalesOrderDetails,
        class: 'navigate-grid',
        widgetData: {
          gridHeight: '112px',
          filterable: false,
          showSearch: false,
          canAdd: false,
          canEdit: false,
          orderControls: false,
          canRemove: false,
          hideActionPanel: true,
          disableColumnSelector: true,
          multiselect: false,
          columns: [
            {
              title: I18n.Forms.StandWorksheetForm.OrderNumber,
              path: 'order_number',
              filterType: CELL_FILTER.string,
              filterParams: {
                quickFilter: true,
              },
              defaultView: true,
              sortable: true,
              headerComponentParams: {
                hintOptions: {
                  description: I18n.Forms.StandWorksheetFormTips.OrderNumberTip,
                  placement: 'top',
                },
              },
              cellClass: (params: CellClassParams): string | string[] => {
                return params.data?.is_post_closure ? 'high-light' : '';
              },
            },
            {
              title: I18n.Forms.StandWorksheetForm.CloseDateTime,
              filterType: CELL_FILTER.dateTime,
              path: 'transaction_date',
              filterParams: {
                filterOptions: ['equals', 'inRangeDate'],
                default: {
                  type: 'inRangeDate',
                },
                step: 1,
              },
              valueGetter: (params: ValueGetterParams): string => {
                const transactionDate = params.data?.transaction_date;

                // Todo
                return formService.companyService.convertUTCDateWithTimeZone(
                  transactionDate,
                  true,
                  TIME_FORMATS.L_LTS,
                );
              },
              defaultView: true,
              sortable: true,
            },
            {
              title: I18n.Forms.StandWorksheetForm.OrderType,
              path: 'xvi_order_type',
              filterType: CELL_FILTER.enum,
              filterParams: {
                values: STAND_WORKSHEET_ORDER_TYPE_OPTIONS_FOR_FILTER,
              },
              defaultView: true,
              sortable: true,
              cellRenderer: (record: ICellRendererParams): string =>
                formService.i18nService.instant(
                  STAND_WORKSHEET_ORDER_TYPE_OPTIONS_FOR_RENDER[record.value],
                ) || startCase(record?.value),
            },
          ],
          userGridOptions: {
            overlayNoRowsTemplate: formService.i18nService.instant(
              I18n.Common.NoRowsToShow,
            ),
            suppressHorizontalScroll: false,
            suppressClickEdit: true,
            suppressRowClickSelection: true,
            defaultColDef: {
              editable: false,
            },
          },
          entity: ENTITY_TYPES.SALE_DEPLETION_ORDER,
          status: (): string => {
            return formService.form
              ? getFormFieldValue(formService.form, 'status')
              : '';
          },
          filter: (): string => {
            return formService.form
              ? `event_id eq in(${getFormFieldValue(
                  formService.form,
                  'event_id',
                )}) and site_id eq in(${getFormFieldValue(formService.form, 'site_id')})`
              : '';
          },
        },
      },
      'virtual.information_list': {
        widget: WIDGET_COMPONENT.edit_grid_custom,
        title: I18n.Forms.StandWorksheetForm.InformationData,
        class: 'navigate-grid',
        widgetData: {
          gridHeight: '112px',
          filterable: false,
          showSearch: false,
          canAdd: false,
          canEdit: false,
          orderControls: false,
          canRemove: false,
          hideActionPanel: true,
          disableColumnSelector: true,
          multiselect: false,
          columns: informationTabledColumn,
          userGridOptions: {
            suppressHorizontalScroll: false,
            suppressClickEdit: true,
            suppressRowClickSelection: true,
            suppressAutoSize: true,
            defaultColDef: {
              editable: true,
            },
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            onDragStopped: (params): void => {
              formService.saveUserConfig(
                USER_CONFIG_MODULE_NAME.STAND_WORK_SHEET_INFORMATION,
              );
            },
          },
          inlineEditOptions: {
            beforeActionMappers: [
              formService.getInformationListInlineChangeMapper(),
            ],
          },
          eventHandlers: {
            [XUX_GRID_ACTIONS.GRID_READY]:
              formService.onInformationGridReadyHandler.bind(formService),
          },
        },
      },
      // this is for only save changed data
      'virtual.changed_items': {
        show: false,
      },
      'virtual.write_error_data_message': {
        widget: WIDGET_COMPONENT.label,
        widgetFamily: WIDGET_FAMILY.LEGACY,
        title: I18n.Forms.StandWorksheetFormErrors.FailedToWriteEndingCounts,
        hiddenObs: formService.hiddenWriteErrorDataMessage$,
        show: true,
        widgetData: {
          itemClass: COMMON_ERROR_MESSAGE_CLASS,
        },
      },
      items: {
        widget: WIDGET_COMPONENT.edit_grid_custom,
        action: formService.entryFormAction$,
        schema: {
          type: 'array',
        },
        required: false,
        title: I18n.Forms.StandWorksheetForm.EntryForm,
        class:
          'no-padding navigate-grid standsheet-entry-form-grid fixed-scrollbar-grid',
        widgetData: {
          useVisibleItemCountDescription: true,
          filterable: true,
          showSearch: true,
          canAdd: false,
          canEdit: false,
          orderControls: false,
          canRemove: false,
          disableColumnSelector: true,
          multiselect: multiSelectable,
          rowSelectableWhenDisabled: true,
          userGridOptions: {
            noGridHeight: true,
            suppressHorizontalScroll: false,
            rowBuffer: SUGGESTED_ROW_BUFFER,
            suppressClickEdit: true,
            suppressRowClickSelection: true,
            suppressAutoSize: true,
            defaultColDef: {
              editable: true,
            },
            rowClassRules: formService.getListGridRowCellClass(),
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            onDragStopped: (params): void => {
              formService.saveUserConfig(
                USER_CONFIG_MODULE_NAME.STAND_WORK_SHEET_ENTRY_FORM,
              );
            },
            onColumnPinned: (params): void => {
              formService.autoAdjustPinnedColumn(
                params?.pinned,
                params?.column?.colId,
                params?.source,
              );
            },
            onColumnMoved: (params): void => {
              formService.autoAdjustMovedColumn(
                params?.toIndex,
                params?.column?.colId,
                params?.source,
              );
            },
          },

          inlineEditOptions: {
            beforeActionMappers: [formService.getInlineChangeMapper()],
          },
          eventHandlers: {
            [XUX_GRID_ACTIONS.GRID_READY]:
              formService.onGridReadyHandler.bind(formService),
          },
          buttons: [
            {
              caption: formService.editCountsButtonCaptionOptions$,
              class: 'btn-primary mr-10',
              position: TOOLBAR_BUTTON_POSITION.HEADER,
              click: formService.toggleViewMode.bind(formService),
              disableWithGrid: false,
              disabledObs: formService.disabledEditCounts$,
              hiddenObs: formService.isFinalStatus$,
              tooltip: formService.i18nService.instant(
                I18n.Common.AutoRefreshingTemporarilyPauses,
              ),
            },
            {
              caption: I18n.Common.ViewSetting,
              class: 'btn-primary mr-10',
              position: TOOLBAR_BUTTON_POSITION.HEADER,
              click: formService.openViewSettingModal.bind(formService),
              disableWithGrid: false,
            },
            {
              caption:
                I18n.Forms.StandWorksheetForm.WriteZerosToBlankEndingCounts,
              position: TOOLBAR_BUTTON_POSITION.HEADER,
              splitDropdown: true,
              dropdownMenuPosition: DROPDOWN_MENU_POSITION.RIGHT,
              flexOrder: BUTTON_FLEX_ORDER.RIGHTMOST,
              class: 'btn-primary btn-group',
              click: formService.writeValueToEndCounts.bind(formService, ''),
              disabledObs: formService.disabledWriteZeroValue$,
              hiddenObs: formService.isFinalStatus$,
              childItems: [
                {
                  caption:
                    I18n.Forms.StandWorksheetForm
                      .WriteBeginCountsToBlankEndingCounts,
                  click: formService.writeValueToEndCounts.bind(
                    formService,
                    'begin',
                  ),
                  hidden:
                    !formService.standWorksheetService.hasBeginCountPermission(),
                },
                {
                  caption:
                    I18n.Forms.StandWorksheetForm
                      .WriteExpectedQuantitiesToBlankEndingCounts,
                  click: formService.writeValueToEndCounts.bind(
                    formService,
                    'expected_qty',
                  ),
                  hidden:
                    !formService.standWorksheetService.hasExpectedQTYPermission(),
                },
              ],
            },
            entryFormActionsButtonConfig,
          ],
          columns: entryFormColumns,
        },
      },
      store_number: {
        required: false,
        show: false,
      },
      cash_count: {
        required: false,
        show: false,
        defaultValue: null,
      },
      miscellaneous: {
        required: false,
        show: false,
        defaultValue: null,
      },
      'virtual.has_pending_transfer_message': {
        widget: WIDGET_COMPONENT.label,
        widgetFamily: WIDGET_FAMILY.LEGACY,
        titleObs: formService.pendingTransferMessage$,
        hiddenObs: formService.hiddenPendingTransferMessage$,
        show: true,
        widgetData: {
          itemClass: GENERAL_ERROR_MESSAGE_CLASS,
          otherTips: formService.i18nService.instant(
            I18n.Common.ClickHereToCompleteThem,
          ),
          clickFn: () => formService.showPendingTransferListDialogHandler(),
        },
      },
    },
    groups: panelGroups,
  };
};
